import type { CampaignAsset } from '@/api/campaigns';

/**
 * Content signature of the asset fields the image-resolution effect actually reads.
 *
 * The effect used to depend on the `assetsData` object identity, which meant ANY
 * refetch of ['campaign-assets'] tore down and re-resolved every image — clearing
 * the resolved URLs, flipping the loading skeleton back on, and re-downloading
 * each carousel card. That is expensive because the backend mints a fresh signed
 * URL per asset on every request, so nothing can be served from the browser cache.
 *
 * Most refetches change nothing the effect cares about. Approve, for instance,
 * only writes status/approved_by_user_id/approved_at/approval_note. Keying on this
 * signature means such refetches are no-ops for image resolution.
 *
 * Deliberately EXCLUDED:
 *  - `signed_url` — regenerated on every request, so including it would defeat the
 *    whole optimisation. It is only an input to resolveUrl; already-resolved URLs
 *    stay valid in state.
 *  - `status` / `approved_at` / `approval_note` — changed by approve, never read here.
 *
 * Deliberately INCLUDED:
 *  - `is_offer_led` / `offer_end_date` — the same effect seeds the offer-led
 *    controls and their loadedValues baseline from the primary asset row, so an
 *    offer-only refetch must re-run it (otherwise the form and isDirty go stale).
 *
 * Lives in its own module (rather than inside PreviewAndAdDetails) so it can be
 * unit-tested without importing that component's heavy browser-only dependencies.
 */
export function assetsContentSignature(assets: CampaignAsset[]): string {
  return JSON.stringify(
    assets.map((a) => [
      a.id,
      a.variant_number,
      a.ratio,
      a.storage_path,
      a.content_type,
      a.position,
      a.post_copies,
      a.headlines,
      a.descriptions,
      a.cta,
      a.url,
      a.legal,
      a.is_offer_led,
      a.offer_end_date,
    ]),
  );
}
